import { TextLabel } from '@core/components/Inputs';
import { TableActionButton, TableActionContainer } from '@core/components/TableAction';
import { DataBR } from '@format';
import { Divider, Grid, makeStyles, Tooltip } from '@material-ui/core';
import { ClearAll, Edit } from '@material-ui/icons';
import { getCurrencySemSinalMonetario } from 'app/utils/monetarioUtil';
import React, { Fragment } from 'react';
import { FaEye, FaInfoCircle, FaPlus, FaSearch } from 'react-icons/fa';
import { VisualizacaoDespesaLista, VisualizacaoDespesaUnica } from './VisualizacaoDespesa';

const useStyles = makeStyles((theme) => ({
  btnContainer: {
    alignSelf: 'center',
  },
  btn: {
    background: '#5890aa',
    color: '#fff',
    width: '100%',
    height: 36,
    borderRadius: 5,
    textTransform: 'initial',
    transition: '0.4s',
    '&:hover': {
      background: '#335674',
    },
  },
  containerExtrato: {
    minHeight: '269px',
    maxHeight: '269px',
    overflow: 'scroll',
  },
  containerExtratoInfo: {
    minHeight: '255px',
    maxHeight: '255px',
  },
  customIcons: {
    fontSize: '0.7em',
  },
  buttonEditDisabled: {
    cursor: 'default',
    color: 'white',
    borderRadius: '30%',
    padding: 4,
    border: 0,
    backgroundColor: 'rgb(157, 157, 157, 0.8)',
    '&:hover': {
      backgroundColor: 'rgb(157, 157, 157, 1)',
      boxShadow: 'none',
      borderColor: 'rgb(157, 157, 157, 0)',
    },
    '&:active': {
      boxShadow: 'none',
      backgroundColor: 'rgb(157, 157, 157, 0.9)',
      borderColor: 'rgb(157, 157, 157, 0)',
    },
    '&:focus': {
      boxShadow: '0 0 0 0.2rem rgb(157, 157, 157, 0.9)',
      borderColor: 'rgb(157, 157, 157, 0)',
    },
  },
}));

export const Receita = ({
  extrato,
  contaReceber,
  exibirTitulo,
  exibirAcoes,
  conciliado,
  handlerVisualizar,
  handlerEditar,
  handlerLimparInformacoes,
  temPermissaoParaVisualizar,
  temPermissaoParaEditar,
  exibirTooltipSemCorrespondencia,
  temPermissaoParaCadastrar,
  handlerLancarContaReceber,
  handlerPesquisar,
}) => {
  const classes = useStyles();
  const tooltipSemCorrespondencia =
    'Este é um lançamento que já foi baixado no sistema, mas que ainda não possui outro ' +
    'semelhante nos extratos importados para que seja conciliado.';

  const disableEditButton =
    contaReceber?.receitaCobranca?.situacaoRemessa === 'GERADA' &&
    contaReceber?.receitaCobranca?.tipoCobranca !== 'PARTNER_BANK';

  const getBaixaInfo = (baixas, prop) => {
    const ultimaDataRecebimento = baixas
      ?.map((baixa) => baixa?.dataRecebimento)
      .sort((a, b) => new Date(b) - new Date(a))[0];

    const ultimaDataLiquidacao = baixas
      ?.map((baixa) => baixa?.dataLiquidacao)
      .sort((a, b) => new Date(b) - new Date(a))[0];

    const somatorioValoresPagos = baixas
      ?.map((baixa) => baixa?.valorPago)
      .reduce((acc, valorPago) => acc + (valorPago || 0), 0);

    if (prop === 'dataRecebimento') {
      return ultimaDataRecebimento;
    } else if (prop === 'dataLiquidacao') {
      return ultimaDataLiquidacao;
    } else if (prop === 'valorPago') {
      return somatorioValoresPagos;
    }
  };

  return (
    <Fragment>
      {exibirTitulo ? (
        <div style={{ margin: '15px 0px' }}>
          <b>RECEITA</b>
        </div>
      ) : (
        <></>
      )}
      <Grid container spacing={3}>
        {exibirTooltipSemCorrespondencia && (
          <Grid item xs={12} style={{ textAlign: 'end' }}>
            <Tooltip title={tooltipSemCorrespondencia} placement="right-end">
              <button type="button" onClick={() => {}}>
                <FaInfoCircle size={18} style={{ color: '#f2994a' }} />
              </button>
            </Tooltip>
          </Grid>
        )}
        <Grid item xs={12}>
          <TextLabel id="contaReceber.nome" label="Cliente" value={contaReceber?.cliente?.pessoa?.nome} />
        </Grid>
        <Grid item xs={6}>
          <TextLabel
            id="dataRecebimento"
            label="Data do recebimento"
            value={DataBR(getBaixaInfo(contaReceber?.baixas, 'dataRecebimento'))}
          />
        </Grid>
        <Grid item xs={6}>
          <TextLabel
            id="dataLiquidacao"
            label="Data da liquidação"
            value={DataBR(getBaixaInfo(contaReceber?.baixas, 'dataLiquidacao'))}
          />
        </Grid>
        <Grid item xs={6}>
          <TextLabel
            id="categoria"
            label="Categoria"
            value={
              contaReceber?.categorias
                ? contaReceber?.categorias.length > 1
                  ? 'Categorias diversas'
                  : `${contaReceber?.categorias[0]?.classe?.indice} ${contaReceber?.categorias[0]?.classe?.nome}`
                : '-'
            }
          />
        </Grid>
        <Grid item xs={6}>
          <TextLabel
            id="contaReceber.valor"
            label="Valor recebido (R$)"
            value={getCurrencySemSinalMonetario(
              contaReceber?.valor ? contaReceber?.valor : getBaixaInfo(contaReceber?.baixas, 'valorPago')
            )}
          />
        </Grid>
        {exibirAcoes ? (
          <Grid item xs={12} style={{ padding: '5px 12px', textAlign: 'end' }}>
            <Fragment key={contaReceber?.id}>
              <TableActionContainer>
                {temPermissaoParaVisualizar && (
                  <TableActionButton
                    title="Visualizar"
                    icon={<FaEye size={16} />}
                    onClick={() => handlerVisualizar(contaReceber)}
                  />
                )}
                {!conciliado && (
                  <>
                    {temPermissaoParaEditar && (
                      <TableActionButton
                        icon={<Edit size={12} className={classes.customIcons} />}
                        onClick={() => {
                          if (!disableEditButton) {
                            handlerEditar(contaReceber);
                          }
                        }}
                        title={
                          disableEditButton
                            ? 'Este boleto já foi incluído em uma remessa. Aguarde o retorno do banco.'
                            : 'Editar'
                        }
                        style={disableEditButton && classes.buttonEditDisabled}
                      />
                    )}
                    {temPermissaoParaVisualizar && (
                      <TableActionButton
                        title="Pesquisar conta"
                        icon={<FaSearch size={16} />}
                        onClick={() => handlerPesquisar()}
                      />
                    )}
                    {temPermissaoParaCadastrar && (
                      <TableActionButton
                        title="Lançar conta"
                        icon={<FaPlus size={16} />}
                        onClick={() => handlerLancarContaReceber()}
                      />
                    )}
                    <TableActionButton
                      title="Limpar informações"
                      icon={<ClearAll size={12} className={classes.customIcons} />}
                      onClick={() => handlerLimparInformacoes(contaReceber)}
                    />
                  </>
                )}
              </TableActionContainer>
            </Fragment>
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </Fragment>
  );
};

const VisualizacaoReceitaDespesa = ({
  contaReceberList,
  contaPagarList,
  conciliado,
  handlerVisualizarReceita,
  handlerVisualizarDespesa,
  temPermissaoParaVisualizarReceita,
  temPermissaoParaVisualizarDespesa,
}) => {
  return (
    <div>
      {contaReceberList && contaReceberList.length === 1 ? (
        <Receita
          contaReceber={contaReceberList[0]}
          exibirTitulo
          exibirAcoes
          temPermissaoParaVisualizar={temPermissaoParaVisualizarReceita}
          conciliado={conciliado}
          handlerVisualizar={handlerVisualizarReceita}
        />
      ) : (
        <VisualizacaoReceitaLista
          exibirTitulo
          contaReceberList={contaReceberList}
          conciliado={conciliado}
          handlerVisualizar={handlerVisualizarReceita}
          temPermissaoParaVisualizar={temPermissaoParaVisualizarReceita}
        />
      )}
      <Divider style={{ margin: '20px 0px' }} />
      {contaPagarList && contaPagarList.length === 1 ? (
        <VisualizacaoDespesaUnica
          contaPagar={contaPagarList[0]}
          exibirTitulo
          exibirAcoes
          temPermissaoParaVisualizar={temPermissaoParaVisualizarDespesa}
          conciliado={conciliado}
          handlerVisualizar={handlerVisualizarDespesa}
        />
      ) : (
        <VisualizacaoDespesaLista
          exibirTitulo
          contaPagarList={contaPagarList}
          conciliado={conciliado}
          temPermissaoParaVisualizar={temPermissaoParaVisualizarDespesa}
          handlerVisualizar={handlerVisualizarDespesa}
        />
      )}
    </div>
  );
};

const VisualizacaoReceitaUnica = ({ extrato, contaReceber, exibirTooltipSemCorrespondencia }) => {
  return (
    <Receita
      extrato={extrato}
      contaReceber={contaReceber}
      exibirTooltipSemCorrespondencia={exibirTooltipSemCorrespondencia}
    />
  );
};

export const VisualizacaoReceitaLista = ({
  contaReceberList,
  conciliado,
  handlerVisualizar,
  handlerEditar,
  handlerLimparInformacoes,
  exibirTitulo,
  handlerLancarContaReceber,
  temPermissaoParaVisualizar,
  temPermissaoParaEditar,
  temPermissaoParaCadastrar,
  exibirAcoes,
  handlerPesquisar,
}) => {
  const classes = useStyles();

  return (
    <>
      {exibirTitulo && (
        <div style={{ margin: '15px 0px' }}>
          <b>RECEITA</b>
        </div>
      )}
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <b>Cliente</b>
        </Grid>
        <Grid item xs={3}>
          <b>Valor recebido (R$)</b>
        </Grid>
        <Grid item xs={3} />
      </Grid>
      <Grid container spacing={3}>
        {contaReceberList?.map((contaReceber, index) => (
          <Grid container key={index}>
            <Grid item xs={6} style={{ padding: '5px 12px' }}>
              <Fragment key={contaReceber?.id}>
                <TextLabel
                  id="contaReceber.nome"
                  label=""
                  value={contaReceber?.cliente?.pessoa?.nome || contaReceber?.cliente?.nome}
                />
              </Fragment>
            </Grid>
            <Grid item xs={3} style={{ padding: '5px 12px' }}>
              <Fragment key={contaReceber?.id}>
                <TextLabel
                  id="contaReceber.valor"
                  label=""
                  value={
                    !!contaReceber?.valor
                      ? getCurrencySemSinalMonetario(contaReceber?.valor)
                      : !!contaReceber?.baixa
                      ? getCurrencySemSinalMonetario(contaReceber?.baixa?.valorPago)
                      : getCurrencySemSinalMonetario(contaReceber?.valorBruto)
                  }
                />
              </Fragment>
            </Grid>
            <Grid item xs={3} style={{ padding: '5px 12px', textAlign: 'end' }}>
              <Fragment key={contaReceber?.id}>
                <TableActionContainer>
                  {temPermissaoParaVisualizar && (
                    <TableActionButton
                      title="Visualizar"
                      icon={<FaEye size={16} />}
                      onClick={() => handlerVisualizar(contaReceber)}
                    />
                  )}
                  {!conciliado && (
                    <>
                      {temPermissaoParaEditar && (
                        <TableActionButton
                          title="Editar"
                          icon={<Edit size={12} className={classes.customIcons} />}
                          onClick={() => handlerEditar(contaReceber)}
                        />
                      )}
                      <TableActionButton
                        title="Limpar informações"
                        icon={<ClearAll size={12} className={classes.customIcons} />}
                        onClick={() => handlerLimparInformacoes(contaReceber)}
                      />
                    </>
                  )}
                </TableActionContainer>
              </Fragment>
            </Grid>
          </Grid>
        ))}
      </Grid>
      {exibirAcoes && (
        <Grid container spacing={3}>
          <Grid item xs={12} style={{ margin: '10px 0px', textAlign: 'end' }}>
            <TableActionContainer>
              {temPermissaoParaVisualizar && (
                <TableActionButton
                  title="Pesquisar conta"
                  icon={<FaSearch size={16} />}
                  onClick={() => handlerPesquisar()}
                />
              )}
              {temPermissaoParaCadastrar && (
                <TableActionButton
                  title="Lançar conta"
                  icon={<FaPlus size={16} />}
                  onClick={() => handlerLancarContaReceber()}
                />
              )}
              <TableActionButton
                title="Limpar informações"
                icon={<ClearAll size={12} className={classes.customIcons} />}
                onClick={() => handlerLimparInformacoes(contaReceberList[0], undefined, contaReceberList)}
              />
            </TableActionContainer>
          </Grid>
        </Grid>
      )}
    </>
  );
};

const VisualizacaoReceita = ({
  extrato,
  handlerVisualizarReceita,
  handlerVisualizarDespesa,
  handlerLimparInformacoes,
  exibirTooltipSemCorrespondencia,
  temPermissaoParaVisualizarReceita,
  temPermissaoParaVisualizarDespesa,
}) => {
  const isReceitaDespesa = extrato?.contaReceberList && extrato?.contaPagarList;

  const visualizacaoUnicaReceita =
    extrato?.contaReceberList && extrato?.contaReceberList.length === 1 && !extrato?.contaReceberList[0].formulario;

  const visualizacaoLista = extrato?.contaReceberList && extrato?.contaReceberList.length > 1 && !isReceitaDespesa;

  return (
    <>
      {visualizacaoUnicaReceita && !isReceitaDespesa && (
        <VisualizacaoReceitaUnica
          extrato={extrato}
          contaReceber={extrato?.contaReceberList[0]}
          exibirTooltipSemCorrespondencia={exibirTooltipSemCorrespondencia}
        />
      )}
      {isReceitaDespesa && (
        <VisualizacaoReceitaDespesa
          contaReceberList={extrato?.contaReceberList}
          contaPagarList={extrato?.contaPagarList}
          conciliado={extrato?.conciliado}
          handlerVisualizarReceita={handlerVisualizarReceita}
          handlerVisualizarDespesa={handlerVisualizarDespesa}
          temPermissaoParaVisualizarReceita={temPermissaoParaVisualizarReceita}
          temPermissaoParaVisualizarDespesa={temPermissaoParaVisualizarDespesa}
        />
      )}
      {visualizacaoLista && (
        <VisualizacaoReceitaLista
          contaReceberList={extrato?.contaReceberList}
          conciliado={extrato?.conciliado}
          handlerVisualizar={handlerVisualizarReceita}
          handlerLimparInformacoes={handlerLimparInformacoes}
          temPermissaoParaVisualizar={temPermissaoParaVisualizarReceita}
        />
      )}
    </>
  );
};

export default VisualizacaoReceita;
